"use client";

import type { BadmintonTemplateData } from "../badminton-template";
import type { Participant } from "../base-match-template";
import type { BasketballTemplateData } from "../basketball-template";
import type { FootballTemplateData } from "../football-template";
import type { PadelTemplateData } from "../padel-template";
import type { PickleballTemplateData } from "../pickleball-template";
import type { SquashTemplateData } from "../squash-template";
import type { TableTennisTemplateData } from "../table-tennis-template";
import { BadmintonCourtPreview } from "./badminton-court-preview";
import { BasketballCourtPreview } from "./basketball-court-preview";
import { FootballPitchPreview } from "./football-pitch-preview";
import { PadelCourtPreview } from "./padel-court-preview";
import { PickleballCourtPreview } from "./pickleball-court-preview";
import { SnookerTablePreview } from "./snooker-table-preview";
import { SquashCourtPreview } from "./squash-court-preview";
import { TableTennisTablePreview } from "./table-tennis-table-preview";
import { TennisCourtPreview } from "./tennis-court-preview";

const normalizeActivityId = (activityId: string) =>
  activityId
    .toLowerCase()
    .replace(/[^a-z-]/g, "")
    .replace(/-/g, "");

export type ActivityCourtPreviewProps = {
  activityId: string;
  data?: unknown;
  mode?: "singles" | "doubles";
  yourTeam: Participant[];
  opponentTeam: Participant[];
  yourWon?: boolean;
  showLabels?: boolean;
  linkToProfiles?: boolean;
  // Snooker specific props
  matchScore?: { yourFrames: number; opponentFrames: number };
  frames?: { yourScore: number; opponentScore: number }[];
  highestBreak?: number;
  // Tennis specific props
  tennisMatchScore?: { yourSets: number; opponentSets: number };
  tennisSets?: { yourScore: number; opponentScore: number }[];
};

export function ActivityCourtPreview({
  activityId,
  data,
  mode,
  yourTeam,
  opponentTeam,
  yourWon,
  showLabels,
  linkToProfiles,
  matchScore,
  frames,
  highestBreak,
  ...props
}: ActivityCourtPreviewProps) {
  const normalized = normalizeActivityId(activityId);

  switch (normalized) {
    case "tennis":
      return (
        <TennisCourtPreview
          mode={mode ?? "singles"}
          yourTeam={yourTeam}
          opponentTeam={opponentTeam}
          yourWon={yourWon}
          showLabels={showLabels}
          linkToProfiles={linkToProfiles}
          matchScore={props.tennisMatchScore}
          sets={props.tennisSets}
        />
      );
    case "badminton": {
      const badmintonData = data as BadmintonTemplateData | null | undefined;
      const badmintonShowScore =
        !!badmintonData && badmintonData.hasScore !== false;
      return (
        <BadmintonCourtPreview
          mode={mode ?? "singles"}
          yourTeam={yourTeam}
          opponentTeam={opponentTeam}
          yourWon={yourWon}
          showLabels={showLabels}
          linkToProfiles={linkToProfiles}
          matchScore={badmintonShowScore ? badmintonData.matchScore : undefined}
          games={badmintonShowScore ? badmintonData.games : undefined}
        />
      );
    }
    case "pickleball": {
      const pickleballData = data as PickleballTemplateData | null | undefined;
      const pickleballShowScore =
        !!pickleballData && pickleballData.hasScore !== false;
      return (
        <PickleballCourtPreview
          mode={mode ?? "singles"}
          yourTeam={yourTeam}
          opponentTeam={opponentTeam}
          yourWon={yourWon}
          showLabels={showLabels}
          linkToProfiles={linkToProfiles}
          matchScore={
            pickleballShowScore ? pickleballData.matchScore : undefined
          }
          games={pickleballShowScore ? pickleballData.games : undefined}
        />
      );
    }
    case "tabletennis": {
      const tableTennisData = data as
        | TableTennisTemplateData
        | null
        | undefined;
      const tableTennisShowScore =
        !!tableTennisData && tableTennisData.hasScore !== false;
      return (
        <TableTennisTablePreview
          mode={mode ?? "singles"}
          yourTeam={yourTeam}
          opponentTeam={opponentTeam}
          yourWon={yourWon}
          showLabels={showLabels}
          linkToProfiles={linkToProfiles}
          matchScore={
            tableTennisShowScore ? tableTennisData.matchScore : undefined
          }
          games={tableTennisShowScore ? tableTennisData.games : undefined}
        />
      );
    }
    case "padel": {
      const padelData = data as PadelTemplateData | null | undefined;
      const padelShowScore = !!padelData && padelData.hasScore !== false;
      return (
        <PadelCourtPreview
          yourTeam={yourTeam}
          opponentTeam={opponentTeam}
          yourWon={yourWon}
          showLabels={showLabels}
          linkToProfiles={linkToProfiles}
          matchScore={padelShowScore ? padelData.matchScore : undefined}
          sets={padelShowScore ? padelData.sets : undefined}
        />
      );
    }
    case "squash": {
      const squashData = data as SquashTemplateData | null | undefined;
      const squashShowScore = !!squashData && squashData.hasScore !== false;
      return (
        <SquashCourtPreview
          yourTeam={yourTeam}
          opponentTeam={opponentTeam}
          yourWon={yourWon}
          linkToProfiles={linkToProfiles}
          matchScore={squashShowScore ? squashData.matchScore : undefined}
          games={squashShowScore ? squashData.games : undefined}
        />
      );
    }
    case "basketball": {
      const basketballData = data as BasketballTemplateData | null | undefined;
      const basketballShowScore =
        !!basketballData && basketballData.hasScore !== false;
      return (
        <BasketballCourtPreview
          yourTeam={yourTeam}
          opponentTeam={opponentTeam}
          yourWon={yourWon}
          linkToProfiles={linkToProfiles}
          matchScore={
            basketballShowScore ? basketballData.matchScore : undefined
          }
          periods={basketballShowScore ? basketballData.periods : undefined}
        />
      );
    }
    case "football": {
      const footballData = data as FootballTemplateData | null | undefined;
      const footballShowScore =
        !!footballData && footballData.hasScore !== false;
      return (
        <FootballPitchPreview
          yourTeam={yourTeam}
          opponentTeam={opponentTeam}
          yourWon={yourWon}
          linkToProfiles={linkToProfiles}
          matchScore={footballShowScore ? footballData.matchScore : undefined}
          periods={footballShowScore ? footballData.periods : undefined}
          penalties={footballShowScore ? footballData.penalties : undefined}
        />
      );
    }
    case "snooker":
      return (
        <SnookerTablePreview
          yourTeam={yourTeam}
          opponentTeam={opponentTeam}
          yourWon={yourWon}
          matchScore={matchScore}
          frames={frames}
          highestBreak={highestBreak}
        />
      );
    default:
      return null;
  }
}
